import React, { type ReactNode } from 'react';
import ReactDataTable, {
  type ConditionalStyles,
  TableColumn,
} from 'react-data-table-component';
import Box from '../../atoms/Box';
import { useTablePreferences } from '../../../customhooks/useTablePreferences';

export type DataTableColumn<TRow> = TableColumn<TRow>;

type DataTableProps<TRow> = {
  columns: DataTableColumn<TRow>[];
  data: TRow[];
  onRowClick?: (row: TRow) => void;
  keyField?: keyof TRow & string;
  striped?: boolean;
  dense?: boolean;
  responsive?: boolean;
  expandableRows?: boolean;
  expandableRowsComponent?: React.ComponentType<any>;
  expandableRowsComponentProps?: Record<string, any>;
  pagination?: boolean;
  paginationDefaultPage?: number;
  paginationPerPage?: number;
  paginationRowsPerPageOptions?: number[];
  /** The rows are one page loaded from the server; use with paginationTotalRows. */
  paginationServer?: boolean;
  paginationTotalRows?: number;
  /**
   * Flip this value to send the table back to paginationDefaultPage, e.g.
   * when filters change and a server-paged table loads page 1 again.
   */
  paginationResetDefaultPage?: boolean;
  paginationComponentOptions?: {
    rowsPerPageText?: string;
    rangeSeparatorText?: string;
  };
  onChangePage?: (page: number) => void;
  onChangeRowsPerPage?: (
    currentRowsPerPage: number,
    currentPage: number
  ) => void;
  noDataComponent?: ReactNode;
  highlightOnHover?: boolean;
  pointerOnHover?: boolean;
  defaultSortFieldId?: string | number;
  defaultSortAsc?: boolean;
  sortServer?: boolean;
  onSort?: (
    column: DataTableColumn<TRow>,
    sortDirection: 'asc' | 'desc'
  ) => void;
  actions?: ReactNode;
  title?: ReactNode;
  /** Per-row styling, e.g. flagging rows that need attention. */
  conditionalRowStyles?: ConditionalStyles<TRow>[];
  /**
   * Stable, unique key (e.g. 'buildings-list') that enables persisting the
   * user's sort field/direction and rows-per-page choice to localStorage.
   * Sort persistence requires columns to have a stable `id` set.
   */
  persistKey?: string;
  /**
   * Set false to persist only rows-per-page, never sort — for tables whose
   * row order is controlled externally (e.g. tree nav list positions).
   */
  persistSort?: boolean;
};

export default function DataTable<TRow>({
  columns,
  data,
  onRowClick,
  keyField,
  striped,
  dense,
  responsive,
  expandableRows,
  expandableRowsComponent,
  expandableRowsComponentProps,
  pagination = true,
  paginationDefaultPage,
  paginationPerPage = 10,
  paginationRowsPerPageOptions = [10, 25, 50],
  paginationServer,
  paginationTotalRows,
  paginationResetDefaultPage,
  paginationComponentOptions,
  onChangePage,
  onChangeRowsPerPage,
  noDataComponent,
  conditionalRowStyles,
  highlightOnHover = true,
  pointerOnHover = true,
  defaultSortFieldId,
  defaultSortAsc = true,
  sortServer,
  onSort,
  actions,
  title,
  persistKey,
  persistSort = true,
}: DataTableProps<TRow>) {
  const { preferences, savePreferences } = useTablePreferences(persistKey);

  // persisted prefs win over prop defaults; only accept a persisted page
  // size that is actually offered by this table
  const effectivePerPage =
    preferences.rowsPerPage &&
    paginationRowsPerPageOptions.includes(preferences.rowsPerPage)
      ? preferences.rowsPerPage
      : paginationPerPage;
  const effectiveSortFieldId =
    (persistSort ? preferences.sortFieldId : undefined) ?? defaultSortFieldId;
  const effectiveSortAsc =
    (persistSort ? preferences.sortAsc : undefined) ?? defaultSortAsc;

  const handleSort = (
    column: DataTableColumn<TRow>,
    sortDirection: 'asc' | 'desc'
  ) => {
    if (persistKey && persistSort && column.id !== undefined) {
      savePreferences({
        sortFieldId: column.id,
        sortAsc: sortDirection === 'asc',
      });
    }
    onSort?.(column, sortDirection);
  };

  const handleChangeRowsPerPage = (
    currentRowsPerPage: number,
    currentPage: number
  ) => {
    if (persistKey) {
      savePreferences({ rowsPerPage: currentRowsPerPage });
    }
    onChangeRowsPerPage?.(currentRowsPerPage, currentPage);
  };

  return (
    <Box
      sx={{
        // MUI Typography swallows row clicks.
        // wrapper that carries data-tag="allowRowEvents".
        // TODO: use MUI pro datagrid instead?
        '& [data-tag="allowRowEvents"] *': {
          pointerEvents: 'none',
        },
        '& [data-tag="allowRowEvents"] button, & [data-tag="allowRowEvents"] a, & [data-tag="allowRowEvents"] input, & [data-tag="allowRowEvents"] textarea, & [data-tag="allowRowEvents"] select, & [data-tag="allowRowEvents"] [role="button"]':
          {
            pointerEvents: 'auto',
          },
        '& .rdt_noData > [role="cell"], & .rdt_noData [role="status"]': {
          width: '100%',
        },
      }}
    >
      <ReactDataTable
        keyField={keyField}
        striped={striped}
        dense={dense}
        responsive={responsive}
        expandableRows={expandableRows}
        expandableRowsComponent={expandableRowsComponent}
        expandableRowsComponentProps={expandableRowsComponentProps}
        columns={columns}
        data={data}
        pagination={pagination}
        paginationDefaultPage={paginationDefaultPage}
        paginationPerPage={effectivePerPage}
        paginationRowsPerPageOptions={paginationRowsPerPageOptions}
        paginationServer={paginationServer}
        paginationTotalRows={paginationTotalRows}
        paginationResetDefaultPage={paginationResetDefaultPage}
        paginationComponentOptions={paginationComponentOptions}
        onChangePage={onChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        highlightOnHover={highlightOnHover}
        pointerOnHover={pointerOnHover}
        noDataComponent={noDataComponent}
        conditionalRowStyles={conditionalRowStyles}
        onRowClicked={onRowClick}
        defaultSortFieldId={effectiveSortFieldId}
        defaultSortAsc={effectiveSortAsc}
        sortServer={sortServer}
        onSort={handleSort}
        actions={actions}
        title={title}
        headerSeparator={false}
        customStyles={{
          headCells: {
            style: { textTransform: 'none' },
          },
        }}
      />
    </Box>
  );
}
